import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// ── Where a completion document is filed (Calem, report fa733604) ──────────────────────────────────
// "It does not give the option to change the storage location. It is wrong, at the moment it is storing
// them in the 'court' folder rather than the 'company --> documents' folder." The destination was the
// server's guess and nothing else: the row said where the file would go and there was no way to say
// otherwise. This is that box — the folder the document will be filed into, and a press opens every
// folder of Our Documents to pick another, with a line to filter them by.
// The Drop File confirm pop-up's own Folder box (src/components/DropProposals/index.tsx), in the size
// the job popups' rows are written in: the same folder icon and chevron, the same filter line, the same
// amber for a destination not yet chosen, and the same dfp_folder_pick / dfp_folder_filter / dfp_no_match
// words. Used by the Complete popup (JobStatusConfirm) and by Closed Jobs' manual-review dialog (Jobs).
// `folders` is the server's own list of the Our Documents tree with each folder's whole path — exactly
// the folders the guess chooses among (server/src/services/jobCompletionDocs.ts ourDocsFolders).

const SHOWN_MAX = 300;        // as the Drop File pop-up lists them: a filter line narrows the rest
const AMBER = '#B45309';      // the colour both popups mark a part of the name that is still missing

export type DocFolder = { id: string; path: string };

/** The path of the folder now chosen; '' when none is. `fallback` is the guess's own path, which the
 *  list does not have to hold (a job loaded before the list arrived still names its destination). */
export const docFolderPath = (folders: DocFolder[], id: string, fallback = ''): string =>
  (id ? folders.find(f => f.id === id)?.path || fallback : '');

export default function DocFolderBox({ folders, value, path, onChange, disabled }: {
  folders: DocFolder[]; value: string; path: string; onChange: (id: string) => void; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const shown = (query ? folders.filter(f => f.path.toLowerCase().includes(query)) : folders).slice(0, SHOWN_MAX);
  const box: React.CSSProperties = {
    padding: '5px 8px', fontSize: 11.5, borderRadius: 7, border: '1px solid var(--border-strong)',
    background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box', width: '100%',
  };
  if (!open) {
    return (
      <div onClick={() => { if (!disabled) { setOpen(true); setQ(''); } }}
        title={t('jc_doc_folder_tip')}
        style={{ ...box, background: 'var(--bg-surface)', cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, opacity: disabled ? 0.6 : 1 }}>
        <i className="ti ti-folder" style={{ fontSize: 13, flexShrink: 0, color: path ? 'var(--accent)' : AMBER }} aria-hidden />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: path ? 'var(--text-primary)' : AMBER }}>
          {path || t('dfp_folder_pick')}
        </span>
        <i className="ti ti-chevron-down" style={{ fontSize: 13, flexShrink: 0, color: 'var(--text-tertiary)' }} aria-hidden />
      </div>
    );
  }
  return (
    <div style={{ width: '100%' }}>
      <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('dfp_folder_filter')}
        autoComplete="off" onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }} style={{ ...box, marginBottom: 3 }} />
      <div style={{ border: '1px solid var(--border-strong)', borderRadius: 7, maxHeight: 180, overflowY: 'auto', background: 'var(--c-gray-50)' }}>
        {shown.map(f => (
          <div key={f.id} onClick={() => { onChange(f.id); setOpen(false); }}
            style={{ padding: '5px 8px', fontSize: 11.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, borderBottom: '1px solid var(--border)',
              background: f.id === value ? 'var(--bg-active)' : 'transparent', fontWeight: f.id === value ? 700 : 400, color: 'var(--text-secondary)' }}>
            {f.id === value && <i className="ti ti-check" style={{ fontSize: 12, flexShrink: 0 }} aria-hidden />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.path}</span>
          </div>
        ))}
        {!shown.length && <div style={{ padding: 8, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('dfp_no_match')}</div>}
      </div>
    </div>
  );
}
